import type { PaginationDto, PaginationResponseDto } from "core/dto";
import { LOCALE, ORDER_BY } from "core/interfaces";
import type { PageMetaDto } from "shared/dto";

import { getMaterialDefault } from "~/common/defaults/material.default";
import { getPaginationDefault } from "~/common/defaults/pagination.default";
import { getPaginationResponseDefault } from "~/common/defaults/pagination-response.default";
import { materialNormalize } from "~/common/helpers/material-adapter";
import type { IdDto } from "~/common/interfaces/dto/id.dto";
import type { CreateMaterialDto } from "~/common/interfaces/dto/materials/create-material.dto";
import type { GetAllMaterialsDto } from "~/common/interfaces/dto/materials/get-all-materials.dto";
import type { UpdateMaterialByIdDto } from "~/common/interfaces/dto/materials/update-material-by-id.dto";
import type { Material, MaterialAdapted } from "~/common/interfaces/entities/material.interface";
import type { Sort } from "~/common/interfaces/sort.interface";

export const useMaterialsStore = defineStore(
	"materials",
	() => {
		const { locale } = useI18n();
		const materialNormalizeWithLocale = materialNormalize.bind(this, locale as Ref<LOCALE>);

		const currentMaterial = ref<MaterialAdapted>(getMaterialDefault());
		const materials = ref<MaterialAdapted[]>([]);
		const meta = ref<PageMetaDto>(getPaginationResponseDefault());
		const recentGetAllQuery = ref<GetAllMaterialsDto & PaginationDto>();
		const tableSort = ref<Sort>({
			columns: [],
			orderBy: ["createdAt", ORDER_BY.DESC]
		});

		const createOne = async (dto: CreateMaterialDto): Promise<MaterialAdapted> => {
			const material = await useNuxtApp().$apiFetch<Material>("/materials", {
				method: "POST",
				body: dto
			});

			return materialNormalizeWithLocale(material);
		};
		const getAll = async (
			dto: GetAllMaterialsDto = {},
			paginationDto?: Partial<PaginationDto>
		): Promise<PaginationResponseDto<MaterialAdapted>> => {
			const [column, orderBy] = tableSort.value.orderBy;

			recentGetAllQuery.value = {
				...getPaginationDefault(),
				...paginationDto,
				...dto,
				column,
				orderBy
			};

			const res = await useNuxtApp().$apiFetch<PaginationResponseDto<Material>>(
				"/materials",
				{ query: recentGetAllQuery.value }
			);
			materials.value = res.data.map(material => materialNormalizeWithLocale(material));
			meta.value = res.meta;

			return res as PaginationResponseDto<MaterialAdapted>;
		};
		const getById = async (id: number): Promise<MaterialAdapted> => {
			const material = await useNuxtApp().$apiFetch<Material>(`/materials/${id}`);

			return materialNormalizeWithLocale(material);
		};
		const patchById = async (
			id: number,
			dto: UpdateMaterialByIdDto
		): Promise<MaterialAdapted> => {
			const material = await useNuxtApp().$apiFetch<Material>(`/materials/${id}`, {
				method: "PATCH",
				body: dto
			});

			return materialNormalizeWithLocale(material);
		};
		const deleteById = async (id: number): Promise<IdDto> => {
			const res = await useNuxtApp().$apiFetch<IdDto>(`/materials/${id}`, {
				method: "DELETE"
			});

			return res;
		};

		return {
			currentMaterial,
			materials,
			meta,
			recentGetAllQuery,
			tableSort,

			createOne,
			getAll,
			getById,
			patchById,
			deleteById
		};
	},
	{
		persist: {
			paths: ["tableSort.orderBy"]
		}
	}
);
